import { doc, getDoc, setDoc } from 'firebase/firestore';
import { firestore } from '../config/firebase';

const COLLECTION = 'brokerDisplayNames';

const PUBLISHABLE_PERSONAS = new Set(['courtier_hypo', 'courtier_immo', 'preteur_prive']);

/**
 * Publish broker display name so clients can show it when picking a broker (e.g. in BrokerPickerModal).
 * Call when broker loads the app so their name is available for existing links that don't have it stored.
 * @param {string} brokerUid - Broker's user UID
 * @param {string} displayName - Display name to show (e.g. from user profile or Firebase Auth)
 * @param {string} [persona] - courtier_hypo | courtier_immo | preteur_prive (public read for deal owner UI)
 */
export async function setBrokerDisplayName(brokerUid, displayName, persona = null) {
  if (!brokerUid) return;
  const ref = doc(firestore, COLLECTION, brokerUid);
  const payload = { displayName: displayName || '', updatedAt: new Date() };
  if (persona && PUBLISHABLE_PERSONAS.has(persona)) {
    payload.persona = persona;
  }
  await setDoc(ref, payload, { merge: true });
}

/**
 * Get broker display name (for client UI when broker link has no brokerDisplayName).
 * @param {string} brokerUid
 * @returns {Promise<string>}
 */
export async function getBrokerDisplayName(brokerUid) {
  if (!brokerUid) return '';
  const ref = doc(firestore, COLLECTION, brokerUid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return '';
  const name = snap.data()?.displayName;
  return typeof name === 'string' ? name.trim() : '';
}

/**
 * Lecture publique : nom + persona publiée (pour libellés « courtier / prêteur » côté propriétaire).
 * @param {string} brokerUid
 * @returns {Promise<{ displayName: string, persona: string | null }>}
 */
export async function getBrokerPublicProfile(brokerUid) {
  if (!brokerUid) return { displayName: '', persona: null };
  const ref = doc(firestore, COLLECTION, brokerUid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return { displayName: '', persona: null };
  const d = snap.data() || {};
  const name = typeof d.displayName === 'string' ? d.displayName.trim() : '';
  const p = typeof d.persona === 'string' && PUBLISHABLE_PERSONAS.has(d.persona) ? d.persona : null;
  return { displayName: name, persona: p };
}
